/**
 * Подложка: фото или скан плана квартиры под чертежом, по которому обводят стены.
 *
 * Картинка хранится в проекте целиком (data URL, уже ужатая — `widgets/underlay`), рядом —
 * где лежит её левый верхний угол на плане и масштаб: сколько миллиметров в пикселе.
 * Масштаб задают по одной известной стене (`calibrate`): отрезок на картинке и его настоящая
 * длина. Видимость и прозрачность — как у любого слоя; на листы PDF и в 3D подложка не идёт.
 */
import { create } from "zustand";
import { EMPTY_UNDERLAY, uid, type UnderlayImage, type UnderlayLayer } from "@/shared/plan";
import type { Pt } from "@/shared/lib/geometry";
import { LocalStorageUtils, type UnderlayImagesById } from "@/shared/lib/storage";

const UNDERLAY_REF_PREFIX = "underlay:";
const MAX_STORED_UNDERLAYS = 10;
let underlayPersistenceEnabled = true;

export const setUnderlayPersistenceEnabled = (enabled: boolean) => {
	underlayPersistenceEnabled = enabled;
};

const writeUnderlayMap = (map: UnderlayImagesById) => {
	const entries = Object.entries(map);
	for (
		let start = Math.max(0, entries.length - MAX_STORED_UNDERLAYS);
		start < entries.length;
		start++
	) {
		const snapshot = Object.fromEntries(entries.slice(start));
		if (LocalStorageUtils.setUnderlayImages(snapshot)) return;
		// Не поместилось в квоту localStorage — отбрасываем самые старые снимки
	}
};

/** Сохранить data URL картинки подложки в отдельном JSON браузера и вернуть объект с `id`. */
export const storeUnderlayImageData = (image: UnderlayImage): UnderlayImage => {
	if (!image.src.startsWith("data:")) return image;
	const id = image.id ?? uid("ul");
	if (!underlayPersistenceEnabled) return { ...image, id };
	const map = LocalStorageUtils.getUnderlayImages();
	delete map[id];
	map[id] = image.src;
	writeUnderlayMap(map);
	return { ...image, id };
};

/** Достать data URL картинки подложки по `id` или ссылке `underlay:<id>` из локального JSON. */
export const resolveUnderlayImage = (image: UnderlayImage | null): UnderlayImage | null => {
	if (!image) return null;
	if (image.src.startsWith("data:")) {
		return storeUnderlayImageData(image);
	}
	const id =
		image.id ??
		(image.src.startsWith(UNDERLAY_REF_PREFIX)
			? image.src.slice(UNDERLAY_REF_PREFIX.length)
			: null);
	if (!id) return image.src ? image : null;
	const dataUrl = LocalStorageUtils.getUnderlayImages()[id];
	if (!dataUrl) return null;
	return { ...image, id, src: dataUrl };
};

/**
 * Убрать тяжёлый Base64 подложки из JSON проекта перед отправкой на сервер или в кэш:
 * сама картинка остаётся в отдельном JSON браузера (`grid-planner:underlays`).
 */
export const dehydrateUnderlayImage = (image: UnderlayImage | null): UnderlayImage | null => {
	if (!image) return null;
	const stored = image.src.startsWith("data:") ? storeUnderlayImageData(image) : image;
	const id =
		stored.id ??
		(stored.src.startsWith(UNDERLAY_REF_PREFIX)
			? stored.src.slice(UNDERLAY_REF_PREFIX.length)
			: undefined);
	if (!id) return stored;
	return {
		id,
		src: `${UNDERLAY_REF_PREFIX}${id}`,
		width: stored.width,
		height: stored.height,
	};
};

export interface UnderlayLayerState extends Omit<UnderlayLayer, "kind"> {
	/** Была ли подложка откалибрована инструментом «Масштаб» по реальной стене */
	calibrated: boolean;
	/** Положить картинку: левый верхний угол в `at`, масштаб `mmPerPixel` */
	setImage: (image: UnderlayImage, at: Pt, mmPerPixel: number) => void;
	/** Сдвинуть подложку: левый верхний угол в точку плана */
	moveTo: (at: Pt) => void;
	/**
	 * Задать масштаб по известному отрезку: точки `from`, `to` на плане и его настоящая
	 * длина, мм. Точка `from` остаётся на месте — картинка растягивается от неё.
	 */
	calibrate: (from: Pt, to: Pt, length: number) => void;
	setOpacity: (opacity: number) => void;
	/** Убрать подложку совсем */
	clear: () => void;

	setVisible: (visible: boolean) => void;
	toggleVisible: () => void;

	restoreUnderlay: (layer: UnderlayLayer) => void;
}

export const useUnderlayStore = create<UnderlayLayerState>((set) => ({
	...EMPTY_UNDERLAY,
	calibrated: false,

	setImage: (image, at, mmPerPixel) =>
		set({
			image: storeUnderlayImageData(image),
			x: at.x,
			y: at.y,
			mmPerPixel,
			visible: true,
			calibrated: false,
		}),

	moveTo: (at) => set({ x: at.x, y: at.y }),

	calibrate: (from, to, length) =>
		set((s) => {
			const measured = Math.hypot(to.x - from.x, to.y - from.y);
			if (measured < 1 || length <= 0) return s;
			const k = length / measured;
			return {
				mmPerPixel: s.mmPerPixel * k,
				x: from.x + (s.x - from.x) * k,
				y: from.y + (s.y - from.y) * k,
				calibrated: true,
			};
		}),

	setOpacity: (opacity) => set({ opacity: Math.min(1, Math.max(0.1, opacity)) }),

	clear: () => set({ image: null, calibrated: false }),

	setVisible: (visible) => set({ visible }),
	toggleVisible: () => set((s) => ({ visible: !s.visible })),

	restoreUnderlay: (layer) =>
		set({
			visible: layer.visible,
			image: resolveUnderlayImage(layer.image),
			x: layer.x,
			y: layer.y,
			mmPerPixel: layer.mmPerPixel,
			opacity: layer.opacity,
			calibrated: layer.mmPerPixel !== 5,
		}),
}));
